/**
 * Protected route component for authentication-required routes
 */

import { forwardRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useEnrichedAuth } from '../../../../hooks/useEnrichedAuth';
import { usePermissions } from '../hooks/usePermissions';
import type { ProtectedRouteProps } from '../types';

export const ProtectedRoute = forwardRef<HTMLDivElement, ProtectedRouteProps>(
  (props, ref) => {
    const {
      children,
      fallback = <div>Access Denied</div>,
      requireAuth = true,
      requiredRoles,
      requiredPermissions,
      redirectTo = '/auth/login',
    } = props;

    const { isAuthenticated, isLoading, isInitializing } = useEnrichedAuth();
    const { canAccess } = usePermissions();
    const location = useLocation();

    // Wait for the SDK to finish its initial session check before making any
    // redirect decisions. The SDK starts with isInitializing=true while it
    // validates the existing session; acting before that completes would
    // incorrectly bounce an already-authenticated user off a deep link.
    if (isLoading || isInitializing) {
      return <div ref={ref}>Loading...</div>;
    }

    // Not authenticated — send to login and preserve the deep link so the
    // login page can navigate back on success.
    if (requireAuth && !isAuthenticated) {
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      const isOnAuthRoute = location.pathname.startsWith('/auth/');
      const loginUrl = isOnAuthRoute
        ? redirectTo
        : `${redirectTo}?redirect=${encodeURIComponent(currentPath)}`;
      return <Navigate to={loginUrl} replace />;
    }

    // Role/permission gate — consumers pass `fallback` for this case (e.g. <AccessDenied />).
    // `requiredRoles`/`requiredPermissions` are any-of: holding ANY one listed role
    // (or permission) grants access. This matches the documented intent in the menu
    // config ("User must have at least one of these roles") and how the sidebar filters
    // items (RoleGuard defaults to hasAnyRole), so a user who sees a menu item — e.g. a
    // BookingAdmin seeing "All Bookings" — is not then bounced to Access Denied on click.
    if (requiredRoles || requiredPermissions) {
      const hasAccess = canAccess(requiredRoles, requiredPermissions, false);

      if (!hasAccess) {
        return <div ref={ref}>{fallback}</div>;
      }
    }

    return <div ref={ref}>{children}</div>;
  }
);

ProtectedRoute.displayName = 'ProtectedRoute';

export default ProtectedRoute;
